import { Injectable, inject } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { AutenticacionService } from '../services/autenticacion.service';
import { AuthStore } from '../../store/auth.store';
import { NetworkService } from '../../shared/services/network.service';

/**
 * Guard que protege las rutas que requieren autenticación.
 * Si el JWT expiró, invalida solo la sesión API (conserva snapshot de campo).
 * Sin red y con snapshot, redirige a Ruta del día en lugar de login.
 */
@Injectable({
  providedIn: 'root'
})
export class AutenticacionGuard implements CanActivate {
  private readonly autenticacionService: AutenticacionService = inject(AutenticacionService);
  private readonly authStore = inject(AuthStore);
  private readonly network = inject(NetworkService);
  private readonly router = inject(Router);

  canActivate(): boolean | Promise<boolean> {
    const irACampoOLogin = (sesionExpirada = false): boolean => {
      if (this.authStore.debeEntrarRutaDelDiaSinRed(this.network.isOnline())) {
        void this.router.navigate(['/ruta-del-dia'], { replaceUrl: true });
      } else {
        void this.router.navigate(['/login'], sesionExpirada ? { queryParams: { sesionExpirada: 'true' } } : undefined);
      }
      return false;
    };

    if (!this.autenticacionService.obtenerEstadoAutenticacion()) {
      return irACampoOLogin();
    }

    if (this.authStore.tokenExpirado()) {
      // Si hay conexión y refresh token disponible, intentar renovar la sesión
      // ANTES de invalidar: evita expulsar a un usuario cuyo refresh sigue vigente
      // (caída real: el guard ganaba la carrera e iba a login aunque el refresh daba 200).
      const refreshToken = this.authStore.obtenerRefreshToken();
      if (this.network.isOnline() && refreshToken) {
        return this.autenticacionService.refrescarToken().then((ok) => {
          if (ok) {
            console.warn('Sesión renovada por refresh en el guard.');
            return this.validarAccesoPostLogin();
          }
          console.warn('Token expirado detectado en el guard. Refresh fallido. Invalidando sesión API (se conserva modo campo).');
          this.authStore.invalidarSesionApiPreservandoCampo();
          return irACampoOLogin(true);
        });
      }

      console.warn('Token expirado detectado en el guard. Invalidando sesión API (se conserva modo campo).');
      this.authStore.invalidarSesionApiPreservandoCampo();
      return irACampoOLogin(true);
    }

    return this.validarAccesoPostLogin();
  }

  /**
   * Validaciones posteriores al login (perfiles, aceptación de términos).
   * Se aplican tanto al entrar con token válido como tras un refresh exitoso.
   */
  private validarAccesoPostLogin(): boolean {
    const usuario = this.autenticacionService.obtenerUsuarioActual();
    const stateUrl = this.router.getCurrentNavigation()?.extractedUrl.toString() || '';

    if (usuario && (!usuario.perfiles || usuario.perfiles.length === 0)) {
      if (!stateUrl.includes('/acceso-denegado')) {
        void this.router.navigate(['/acceso-denegado']);
        return false;
      }
      return true;
    }

    if (usuario && !usuario.acepta_terminos) {
      const esAsesor = usuario.perfiles.some(p => p.id_perfil === 1);
      if (esAsesor) {
        if (!stateUrl.includes('/terminos')) {
          void this.router.navigate(['/terminos']);
          return false;
        }
      }
    }

    return true;
  }
}
